/**
 * @file ButtonWrapper.tsx
 * @description Flexible button/link component with CMS integration.
 * Automatically renders as Link or button based on props, with variant styling and accessibility features.
 */

'use client';

import { JSX } from 'react';
import Link from 'next/link';
import { IEnhancedCta } from '@/.generated';
import { buttonVariants } from './ButtonWrapper.styles';
import { getCSLPAttributes } from '@/utils/type-guards';
import { trackAdobeClick, trackAdobeDownload, getDocumentNameFromUrl, ClickType } from '@/utils/adobe';
import { useLocalizedHref } from '@/lib/hooks/useLocalizedHref';
import { getFileType } from '@/components/primitives/FileIcon';
import { cn } from 'tailwind-variants';

// Returns the href when it points to a known document type (pdf/doc/xls/ppt…), else undefined
const getDocumentHref = (href?: string): string | undefined => {
  if (!href) return undefined;
  // Strip query string and hash before resolving the extension
  const pathname = href.split('?')[0].split('#')[0];
  return getFileType(pathname) ? href : undefined;
};

export interface ButtonWrapperProps extends React.HTMLAttributes<HTMLButtonElement> {
  /** Enhanced CTA object from Contentstack */
  cta?: IEnhancedCta;
  /** Direct href URL (alternative to CTA object) */
  href?: string;
  /** Button text/label */
  customLabel?: string;
  /** Whether button is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Click handler (for button mode) */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Button type (for button mode) */
  type?: 'button' | 'submit' | 'reset';
  /** Custom variant override */
  customVariant?: IEnhancedCta['cta_variant'];
  /** Custom size override */
  customSize?: IEnhancedCta['cta_size'];
  /** Custom Show/Hide New Tab Icon */
  showNewTabIcon?: boolean;
  /** Analytics click name — falls back to the link/button title */
  clickName?: string;
  /** Analytics click type — falls back to `'link'` when an href exists, otherwise `'button'` */
  clickType?: ClickType;
  /** Analytics click location — falls back to `'body'` */
  clickLocation?: string;
  /** Custom content rendered inside the button/link; overrides CMS-driven label and icons */
  children?: React.ReactNode;
}

/**
 * Flexible button/link component that adapts based on provided props.
 * Supports CMS-driven CTAs with variants, sizes, and accessibility features.
 *
 * @example
 * // As a link with CMS data
 * <ButtonWrapper cta={ctaFromCMS} />
 *
 * // As a button with onClick
 * <ButtonWrapper customLabel="Click me" onClick={handleClick} />
 */
export const ButtonWrapper = ({
  cta,
  href,
  customLabel,
  disabled = false,
  className,
  onClick,
  type = 'button',
  customVariant,
  customSize,
  showNewTabIcon = true,
  clickName,
  clickType,
  clickLocation,
  children,
}: ButtonWrapperProps): JSX.Element => {
  const pageRef = cta?.page_reference?.[0];

  // Default values
  const defaultVariant = 'primary';
  const defaultSize = 'variable';

  // Extract CTA properties
  const opensInNewTab = cta?.opens_in_new_tab || false;
  const ctaVariant = cta?.cta_variant || customVariant || defaultVariant;
  const ctaSize = cta?.cta_size || customSize || defaultSize;

  // Determine rendering mode (link vs button)
  // pageRefUrl takes highest priority, then cta.link.href, then custom href prop
  const isLink = !!pageRef?.url || !!href || !!cta?.link?.href;
  const rawHref = pageRef?.url || cta?.link?.href || href || undefined;
  // Resolve the locale prefix from the reference's own locale (page references) or the
  // routes index (manual URLs) so English-only links aren't given a broken /es prefix.
  const linkHref = useLocalizedHref(rawHref, {
    fromPageReference: !!pageRef?.url,
    referenceLocale: pageRef?.locale,
  });
  const linkTitle = cta?.link?.title || customLabel || undefined;

  if (!linkTitle && !children) return <></>; // Require either a label or children for content
  if (!linkHref && !onClick) return <></>; // Early return if no href or onClick provided

  // Determine new tab behavior
  const shouldOpenInNewTab = opensInNewTab && isLink;

  // Resolve analytics values once — reused in commonProps and trackAdobeClick
  const resolvedClickName = clickName || linkTitle || '';
  const resolvedClickType: ClickType = clickType ?? (isLink ? 'link' : 'button');
  const resolvedClickLocation = clickLocation ?? 'body';
  // A link pointing to a document additionally fires a Download event
  const documentHref = isLink ? getDocumentHref(rawHref) : undefined;

  // Pushes a click event to the Adobe data layer; forwards the event so clickLocation/clickType auto-derive from [data-id]
  const handleAdobeClick = (e?: React.SyntheticEvent<Element>) => {
    if (documentHref) {
      // trackAdobeDownload fires the Click event first, then the Download event
      trackAdobeDownload(
        {
          clickName: resolvedClickName,
          documentName: getDocumentNameFromUrl(documentHref),
          clickType: clickType ?? 'file-download',
          clickLocation,
        },
        e
      );
      return;
    }
    trackAdobeClick(
      {
        clickName: resolvedClickName,
        clickType,
        clickLocation,
      },
      e
    );
  };

  // Click handler for button mode only — links don't support disabled state.
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    handleAdobeClick(e);
    onClick?.(e);
  };

  const base = buttonVariants({
    variant: ctaVariant,
    size: ctaSize,
    class: className,
  });

  // Common props for both button and link
  const commonProps = {
    className: base,
    'aria-label': shouldOpenInNewTab ? `${linkTitle ?? ''} (Opens in a new tab)`.trim() : linkTitle,
    clickname: resolvedClickName,
    clicktype: resolvedClickType,
    clicklocation: resolvedClickLocation,
    ...getCSLPAttributes(cta?.$?.link),
  };

  const ctaContent = () => {
    return (
      <>
        {cta?.has_font_awesome_icons && cta?.left_font_awesome_icon_class && (
          <span aria-hidden="true" className="me-2">
            <i className={cn(cta.left_font_awesome_icon_class, 'text-inherit')}></i>
          </span>
        )}
        {linkTitle}
        {cta?.has_font_awesome_icons && cta?.right_font_awesome_icon_class && (
          <span aria-hidden="true" className="ms-2">
            <i className={cn(cta.right_font_awesome_icon_class, 'text-inherit')}></i>
          </span>
        )}
        {showNewTabIcon && shouldOpenInNewTab && !documentHref && (
          <span aria-hidden="true" className="ms-2">
            <i className="fa-solid fa-arrow-up-right text-inherit"></i>
          </span>
        )}
      </>
    );
  };

  // Render as Link
  if (isLink && linkHref) {
    return (
      <Link
        href={linkHref}
        target={shouldOpenInNewTab ? '_blank' : undefined}
        rel={shouldOpenInNewTab ? 'noopener noreferrer' : undefined}
        onClick={handleAdobeClick}
        {...commonProps}
      >
        {children ?? ctaContent()}
      </Link>
    );
  }

  // Render as Button
  return (
    <button
      type={type}
      onClick={handleClick}
      disabled={disabled}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
      {...commonProps}
    >
      {children ?? ctaContent()}
    </button>
  );
};
